import React, { useEffect, useState } from "react";
import StatusBadge from "../components/common/StatusBadge";
import { useAdminApi } from "../hooks/useAdminApi";
import {
	DashboardPayload,
	DashboardStats,
	EventSummary,
	OrderSummary,
	PayoutSummary,
} from "../types/admin";
import { SampleStatusCell } from "../components/common/DataTable";

const currency = (value?: number | null) =>
	`¥${Math.round(value ?? 0).toLocaleString()}`;
const formatDate = (iso?: string | null) =>
	iso ? new Date(iso).toLocaleString() : "-";

const StatCard: React.FC<{
	label: string;
	value: string;
	sub?: string;
	tone?: "primary" | "warning" | "danger" | "muted";
}> = ({ label, value, sub, tone = "primary" }) => {
	const toneClass = {
		primary: "text-primary",
		warning: "text-amber-600",
		danger: "text-rose-600",
		muted: "text-muted",
	}[tone];

	return (
		<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
			<p className="text-xs uppercase tracking-wide text-muted">{label}</p>
			<p className={`mt-2 text-2xl font-semibold ${toneClass}`}>{value}</p>
			{sub && <p className="mt-1 text-xs text-muted">{sub}</p>}
		</div>
	);
};

const MiniTable: React.FC<{
	title: string;
	columns: string[];
	rows: React.ReactNode[][];
}> = ({ title, columns, rows }) => (
	<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
		<div className="mb-3 flex items-center justify-between">
			<h3 className="text-lg font-semibold text-ink">{title}</h3>
		</div>
		<div className="overflow-x-auto">
			<table className="min-w-full divide-y divide-surface-border text-sm">
				<thead className="bg-surface-alt text-left text-xs uppercase tracking-wide text-muted">
					<tr>
						{columns.map((col) => (
							<th key={col} className="px-3 py-2 font-semibold">
								{col}
							</th>
						))}
					</tr>
				</thead>
				<tbody className="divide-y divide-surface-border">
					{rows.length === 0 && (
						<tr>
							<td
								className="px-3 py-3 text-center text-muted"
								colSpan={columns.length}
							>
								データがありません
							</td>
						</tr>
					)}
					{rows.map((row, idx) => (
						<tr key={idx} className="hover:bg-surface-alt">
							{row.map((cell, cidx) => (
								<td key={cidx} className="px-3 py-2 align-top">
									{cell}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	</div>
);

const DashboardPage: React.FC = () => {
	const api = useAdminApi();
	const [payload, setPayload] = useState<DashboardPayload | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const refresh = async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await api.dashboard();
			setPayload(data);
		} catch (err) {
			console.error("Failed to load dashboard", err);
			setError("ダッシュボードの読み込みに失敗しました");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const stats: DashboardStats | null = payload?.stats ?? null;
	const recentPayouts = payload?.recent_payouts?.data ?? [];
	const recentOrders = payload?.recent_orders?.data ?? [];
	const upcomingEvents = payload?.upcoming_events?.data ?? [];

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm text-muted">実データ連携済みダッシュボード</p>
					<h1 className="text-2xl font-semibold text-ink">Admin Dashboard</h1>
				</div>
				<div className="flex items-center gap-2">
					{loading ? (
						<StatusBadge label="loading" tone="yellow" />
					) : (
						<StatusBadge label="live" tone="green" />
					)}
					<button
						onClick={refresh}
						className="rounded-xl border border-surface-border px-3 sm:px-4 py-2 text-sm font-semibold text-primary"
					>
						再読込
					</button>
				</div>
			</div>

			{error && (
				<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
					{error}
				</div>
			)}

			<div className="grid gap-4 md:grid-cols-4">
				<StatCard
					label="7日間売上"
					value={currency(stats?.revenue_7d)}
					sub={`完了注文 ${stats?.orders_completed_7d ?? 0} 件`}
				/>
				<StatCard
					label="平均注文額"
					value={currency(stats?.avg_order_value_7d)}
					tone="muted"
				/>
				<StatCard
					label="未承認オーガナイザー"
					value={`${stats?.pending_organizers ?? 0} 件`}
					tone="warning"
				/>
				<StatCard
					label="pending 入金申請"
					value={`${stats?.pending_payouts ?? 0} 件`}
					tone="warning"
				/>
			</div>

			<div className="grid gap-4 md:grid-cols-3">
				<StatCard
					label="ドラフトイベント"
					value={`${stats?.draft_events ?? 0}`}
					sub="準備中"
					tone="muted"
				/>
				<StatCard
					label="公開中イベント"
					value={`${stats?.published_events ?? 0}`}
					sub="ticketing"
					tone="primary"
				/>
				<StatCard
					label="失敗決済 (24h)"
					value={`${stats?.failed_payments_24h ?? 0}`}
					tone="danger"
					sub="要調査"
				/>
			</div>

			<MiniTable
				title="最新の入金申請"
				columns={["申請ID", "オーガナイザー", "金額", "状態", "申請日"]}
				rows={(recentPayouts as PayoutSummary[]).map((p) => [
					<span key="id" className="font-semibold text-ink">
						{p.external_id ?? p.id}
					</span>,
					<span key="org">{p.organizer?.organizer_name ?? "-"}</span>,
					<span key="amt" className="tabular-nums">
						{currency(Number(p.amount ?? 0))}
					</span>,
					<SampleStatusCell key="st" status={p.status} />,
					<span key="dt" className="text-xs text-muted">
						{formatDate(p.requested_at)}
					</span>,
				])}
			/>

			<MiniTable
				title="最新の注文"
				columns={["注文ID", "購入者", "イベント", "合計", "状態"]}
				rows={(recentOrders as OrderSummary[]).map((o) => [
					<span key="id" className="font-semibold text-ink">
						{o.external_id ?? o.id}
					</span>,
					<span key="buyer">{o.customer_email ?? o.user?.email ?? "-"}</span>,
					<span key="ev">{o.event?.title ?? "-"}</span>,
					<span key="amt" className="tabular-nums">
						{currency(Number(o.total_amount ?? 0))}
					</span>,
					<span key="st">
						<StatusBadge
							label={o.status}
							tone={
								o.status === "completed"
									? "green"
									: o.status === "pending"
									? "yellow"
									: "red"
							}
						/>
					</span>,
				])}
			/>

			<MiniTable
				title="公開中・直近イベント"
				columns={["イベント", "オーガナイザー", "公開日時", "チケット種"]}
				rows={(upcomingEvents as EventSummary[]).map((e) => [
					<div key="title" className="space-y-1">
						<div className="font-semibold text-ink">{e.title}</div>
						<div className="text-xs text-muted">{e.external_id ?? e.id}</div>
					</div>,
					<span key="org">{e.organizer?.organizer_name ?? "-"}</span>,
					<span key="dt" className="text-xs text-muted">
						{formatDate(e.publication_date_time ?? e.created_at)}
					</span>,
					<span key="tt" className="text-xs text-muted">
						{e.ticket_types_count ?? 0} 種類
					</span>,
				])}
			/>

			{loading && (
				<div className="text-sm text-muted">Loading dashboard data...</div>
			)}
		</div>
	);
};

export default DashboardPage;
